import {
  UilFilter,
  UilImport,
} from '@iconscout/react-unicons';
import {
  Box,
  Button,
  Notification,
  TextField,
  Typography,
  Waiting,
} from '@xbotvn/mui';
import moment from 'moment-timezone';
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { statisticsApi } from '../../../../api';
import Table from '../../../../components/Table';

import exportHandler from './export';

const TIMEZONE = 'Asia/Ho_Chi_Minh';

const emptyTotals = {
  traditional: 0,
  children: 0,
  total: 0,
};

const matchesEmail = (email, filter) => email.toLowerCase().includes(filter.trim().toLowerCase());

export default function Books() {
  const {
    activeUnit,
  } = useSelector(({ user }) => ({
    activeUnit: user.activeUnit,
  }));
  const [filters, setFilters] = useState({
    email: '',
    from: '',
    to: '',
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableEmailFilter, setTableEmailFilter] = useState('');
  const latestRequest = useRef(0);

  const loadReport = async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    if (!activeUnit) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await statisticsApi.getBookStatistics({
        condition: {
          unitId: activeUnit,
          email: filters.email.trim(),
          start: moment.tz(filters.from, 'YYYY-MM-DD', true, TIMEZONE)
            .startOf('day')
            .toISOString(),
          end: moment.tz(filters.to, 'YYYY-MM-DD', true, TIMEZONE)
            .add(1, 'day')
            .startOf('day')
            .toISOString(),
        },
      });

      if (requestId !== latestRequest.current) return;

      setRows(data.records || []);
    } catch (error) {
      if (requestId !== latestRequest.current) return;

      setRows([]);
      Notification.error(error?.response?.data?.errors?.message || 'Không thể tải dữ liệu thống kê');
    } finally {
      if (requestId === latestRequest.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    latestRequest.current += 1;
    setRows([]);
    setLoading(false);
  }, [activeUnit]);

  const handleFilter = () => {
    if (!filters.from || !filters.to) {
      Notification.warn('Vui lòng chọn khoảng ngày');
      return;
    }
    if (filters.from > filters.to) {
      Notification.warn('Từ ngày không được lớn hơn Đến ngày');
      return;
    }
    loadReport();
  };

  const filteredRows = useMemo(() => (tableEmailFilter.trim()
    ? rows.filter((row) => matchesEmail(row.email, tableEmailFilter))
    : rows), [rows, tableEmailFilter]);

  const totals = useMemo(() => filteredRows.reduce((result, row) => ({
    traditional: result.traditional + row.traditional,
    children: result.children + row.children,
    total: result.total + row.total,
  }), emptyTotals), [filteredRows]);

  const columns = useMemo(() => [
    {
      Header: 'STT',
      disableFilters: true,
      style: { width: 80 },
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: 'Email',
      accessor: 'email',
      filter: (tableRows, columnId, value) => tableRows.filter(
        (row) => matchesEmail(row.values[columnId], value),
      ),
      // eslint-disable-next-line react/prop-types
      Filter: ({ column: { filterValue, setFilter } }) => (
        <TextField
          value={filterValue || ''}
          onChange={(event) => {
            setFilter(event.target.value);
            setTableEmailFilter(event.target.value);
          }}
        />
      ),
    },
    {
      Header: 'Sách truyền thống',
      accessor: 'traditional',
      disableFilters: true,
    },
    {
      Header: 'Sách cho trẻ',
      accessor: 'children',
      disableFilters: true,
    },
    {
      Header: 'Tổng số',
      accessor: 'total',
      disableFilters: true,
    },
  ], []);

  return (
    <Box sx={{ color: '#25324a', position: 'relative' }}>
      {loading && <Waiting fullscreen />}
      <Box sx={{ padding: '8px 8px 20px', borderBottom: '1px solid #e8edf4' }}>
        <Typography variant="h5" fontWeight={600}>
          Thống kê sách số hóa theo tài khoản
        </Typography>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          lg: 'minmax(240px, 1.35fr) repeat(2, minmax(180px, 1fr)) auto',
        },
        gap: '12px',
        alignItems: 'end',
        padding: '20px 8px 16px',
      }}
      >
        <TextField
          size="small"
          sx={{ '& .MuiOutlinedInput-input': { height: 17 } }}
          label="Tài khoản email"
          placeholder="Tìm theo email..."
          value={filters.email}
          onChange={(event) => setFilters((current) => ({
            ...current,
            email: event.target.value,
          }))}
        />
        <TextField
          size="small"
          sx={{ '& .MuiOutlinedInput-input': { height: 17 } }}
          label="Từ ngày"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filters.from}
          onChange={(event) => setFilters((current) => ({
            ...current,
            from: event.target.value,
          }))}
        />
        <TextField
          size="small"
          sx={{ '& .MuiOutlinedInput-input': { height: 17 } }}
          label="Đến ngày"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={filters.to}
          onChange={(event) => setFilters((current) => ({
            ...current,
            to: event.target.value,
          }))}
        />
        <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<UilFilter />}
            disabled={loading}
            onClick={handleFilter}
          >
            Lọc dữ liệu
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<UilImport />}
            disabled={loading}
            onClick={() => exportHandler({
              rows: filteredRows,
              totals,
              onSuccess: () => Notification.success('Xuất Excel thành công'),
            })}
          >
            Xuất Excel
          </Button>
        </Box>
      </Box>

      <Box sx={{ padding: '0 8px 14px', color: '#596579' }}>
        <strong>{totals.total}</strong>
        {' sách · '}
        <strong>{filteredRows.length}</strong>
        {' tài khoản'}
      </Box>

      <Table
        columns={columns}
        data={filteredRows}
        autoResetPage
        alwaysShowPagination
      />
    </Box>
  );
}
